import { useCallback, useState } from 'react'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import FmdGoodOutlined from '@mui/icons-material/FmdGoodOutlined'
import Avatar from '@mui/material/Avatar'
import { useFormatMessage } from '@/translations/useFormatMessage'
import type { LocationData, MenuSchedule } from '@/shared/api/location'
import { useAppDispatch, useAppSelector } from '@/store'
import { sizing } from '@/styles/designTokens'
import {
	selectDineInTableName,
	selectSelectedMenuSchedule,
	selectCartItemCount,
	setMenuSchedule,
} from '@/store/ticketSlice'
import { toggleCartDrawer } from '@/store/uiSlice'
import { SignInModal } from '@/components/auth/SignInModal'
import { AccountPopover } from '@/components/auth/AccountPopover'
import { CartButton } from './CartButton'
import { TablePill } from './TablePill'
import { MenuSchedulePill } from './MenuSchedulePill'
import {
	HeaderRoot,
	ExpandedOuter,
	ExpandedContent,
	HeaderRow,
	BrandingBox,
	LocationName,
	LocationAddress,
	SchedulePillDesktopWrap,
	SchedulePillMobileWrap,
	SignInButton,
	SignInButtonText,
	SignInIconButton,
	CompactOuter,
	CompactContent,
} from './MenuHeader.styled'

function buildAddressLine(location: LocationData): string {
	let address = ''

	if (location.addressSelection === 'business') {
		const streetCity = [location.addressLine1, location.addressLine2, location.city].filter(
			Boolean,
		)
		const stateZip = [location.state, location.zip].filter(Boolean).join(' ')
		if (stateZip) streetCity.push(stateZip)
		address = streetCity.join(', ')
	} else if (location.addressSelection === 'custom') {
		address = location.customAddress ?? ''
	}

	const phone = location.oloContactPhone ?? ''
	if (address && phone) return `${address} | ${phone}`
	return address || phone
}

interface MenuHeaderProps {
	location?: LocationData
	schedules?: MenuSchedule[]
	isCompact?: boolean
}

export default function MenuHeader({ location, schedules, isCompact = false }: MenuHeaderProps) {
	const dispatch = useAppDispatch()
	const { t } = useFormatMessage()
	const [isSignInModalOpen, setIsSignInModalOpen] = useState(false)
	const [accountPopoverAnchorEl, setAccountPopoverAnchorEl] = useState<HTMLElement | null>(null)

	const selectedMenuSchedule = useAppSelector(selectSelectedMenuSchedule)
	const dineInTableName = useAppSelector(selectDineInTableName)
	const authUser = useAppSelector((state) => state.auth.user)
	const cartItemCount = useAppSelector(selectCartItemCount)

	const handleCartClick = useCallback(() => dispatch(toggleCartDrawer()), [dispatch])

	const pillSchedules = schedules ?? location?.menuSchedules ?? []
	const activeSchedule =
		selectedMenuSchedule && pillSchedules.some((s) => s.ref === selectedMenuSchedule.ref)
			? selectedMenuSchedule
			: (pillSchedules[0] ?? null)

	const handleAccountClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
		setAccountPopoverAnchorEl(e.currentTarget)
	}, [])

	const handleCloseAccountPopover = useCallback(() => {
		setAccountPopoverAnchorEl(null)
	}, [])

	const addressLine = location ? buildAddressLine(location) : ''

	const handleScheduleSelect = useCallback(
		(s: MenuSchedule) => dispatch(setMenuSchedule(s)),
		[dispatch],
	)

	const handleSignIn = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			if (authUser) {
				handleAccountClick(e)
			} else if (location?.locationRef) {
				setIsSignInModalOpen(true)
			}
		},
		[authUser, location, handleAccountClick],
	)

	const handleCloseSignInModal = useCallback(() => {
		setIsSignInModalOpen(false)
	}, [])

	if (!location) return null

	return (
		<>
			<SignInModal
				isOpen={isSignInModalOpen}
				onClose={handleCloseSignInModal}
				locationRef={location.locationRef}
				locationId={location.id}
			/>
			<AccountPopover
				isOpen={Boolean(accountPopoverAnchorEl)}
				anchorEl={accountPopoverAnchorEl}
				onClose={handleCloseAccountPopover}
			/>
			<HeaderRoot data-testid="menu-header">
				{/* ── Compact row (scrolled state): logo + schedule inline, no pill styling ── */}
				<CompactOuter isCompact={isCompact}>
					<CompactContent isCompact={isCompact}>
						<Avatar
							src={location.logoUrl ?? location.oloLogoUrl ?? undefined}
							alt={location.name ?? ''}
							sx={{
								flexShrink: 0,
								width: sizing.avatarCompact,
								height: sizing.avatarCompact,
							}}
						>
							{!(location.logoUrl ?? location.oloLogoUrl) && <FmdGoodOutlined />}
						</Avatar>

						{!dineInTableName && activeSchedule && (
							<MenuSchedulePill
								schedule={activeSchedule}
								schedules={pillSchedules}
								onSelect={handleScheduleSelect}
								compact
							/>
						)}
					</CompactContent>
				</CompactOuter>

				{/* ── Expanded content (default state) ── */}
				<ExpandedOuter isCompact={isCompact}>
					<ExpandedContent isCompact={isCompact}>
						{/* Row 1: branding + right actions */}
						<HeaderRow>
							<Avatar
								src={location.logoUrl ?? location.oloLogoUrl ?? undefined}
								alt={location.name ?? ''}
								sx={{
									flexShrink: 0,
									width: { xs: sizing.avatarMobile, lg: sizing.avatarDesktop },
									height: { xs: sizing.avatarMobile, lg: sizing.avatarDesktop },
								}}
							>
								{!(location.logoUrl ?? location.oloLogoUrl) && <FmdGoodOutlined />}
							</Avatar>

							<BrandingBox hasSignIn>
								<LocationName>{location.name}</LocationName>
								<LocationAddress
									data-testid="location-address"
									title={addressLine || undefined}
								>
									{addressLine}
								</LocationAddress>
							</BrandingBox>

							{!dineInTableName && activeSchedule && (
								<SchedulePillDesktopWrap>
									<MenuSchedulePill
										schedule={activeSchedule}
										schedules={location.menuSchedules}
										onSelect={handleScheduleSelect}
									/>
								</SchedulePillDesktopWrap>
							)}

							{dineInTableName && <TablePill />}

							<CartButton itemCount={cartItemCount} onClick={handleCartClick} />

							{/* Sign-in control always renders (not gated by signInPromptEnabled, which is checkout-only) */}
							<SignInButton
								data-testid="sign-in-pill"
								onClick={handleSignIn}
								aria-label={
									authUser
										? t('component:menuHeader:account:label')
										: t('component:menuHeader:signIn:label')
								}
							>
								<PersonOutlinedIcon sx={{ fontSize: sizing.navIcon }} />
								<SignInButtonText>
									{authUser
										? authUser.name ||
											authUser.email ||
											authUser.phone ||
											t('component:menuHeader:account:label')
										: t('component:menuHeader:signIn:label')}
								</SignInButtonText>
							</SignInButton>

							<SignInIconButton
								data-testid="sign-in-icon"
								onClick={handleSignIn}
								aria-label={
									authUser
										? t('component:menuHeader:account:label')
										: t('component:menuHeader:signIn:label')
								}
							>
								<PersonOutlinedIcon />
							</SignInIconButton>
						</HeaderRow>

						{/* Row 2 (tablet/mobile only): full-width schedule pill */}
						{!dineInTableName && activeSchedule && (
							<SchedulePillMobileWrap>
								<MenuSchedulePill
									schedule={activeSchedule}
									schedules={location.menuSchedules}
									onSelect={handleScheduleSelect}
									fullWidth
								/>
							</SchedulePillMobileWrap>
						)}
					</ExpandedContent>
				</ExpandedOuter>
			</HeaderRoot>
		</>
	)
}
